import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseHttpService } from '@core/data/http/base-http.service';
import { SubscriptionPlan } from '@core/models/billing/subscription-plan';
import { Observable, catchError, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BillingDataService extends BaseHttpService {
    public static invoiceDownloadUrl = '/billing/invoices/:invoiceId/pdf?access_token=:access_token';
    public static invoiceStatementDownloadUrl = '/billing/invoices/:invoiceId/deliveries';

    private urls = {
        billing: '/billing',
        billingGet: '/billing?history=latest',
        billingKey: '/billing/key',
        billingEstimate: '/billing/estimate',
        billingInvoice: '/billing/invoices/:invoiceId',
        billingInvoices: '/billing/invoices',
        billingStatement: '/billing/invoices/:invoiceId/deliveries',
        billingUser: '/billing/users',
        billingPlan: '/billing/:planId',
        billingReport: '/billing/report?start_month=:startMonth&end_month=:endMonth',
    };

    fetchKey(token?: 'plaid'): Observable<any> {
        let url: string = this.urls.billingKey;
        if (token) {
            url = `${url}?token=${token}`;
        }
        return this.get(url);
    }

    fetchPlaidKey() {
        return this.fetchKey('plaid');
    }

    fetchStripeKey() {
        return this.fetchKey();
    }

    fetchBilling(): Observable<any> {
        return this.get(this.urls.billingGet);
    }

    fetchBillingEstimate(): Observable<any> {
        return this.get(this.urls.billingEstimate);
    }

    fetchInvoice(invoiceId: string): Observable<any> {
        return this.get(
            this.urls.billingInvoice.replace(':invoiceId', invoiceId)
        );
    }

    fetchInvoices(): Observable<any> {
        return this.get(this.urls.billingInvoices);
    }

    updateBilling(billingDetails): Observable<any> {
        return this.put(this.urls.billing, billingDetails);
    }

    fetchUsers(request: { page?: number, all?: boolean}): Observable<any> {
        // Default as first page
        if (!request) {
            request = { page: 1 };
        }

        const requestURL: string =
            this.urls.billingUser + this.toQueryParams(request);
        return this.get(requestURL);
    }

    subscribe(billingDetails) {
        return this.post(this.urls.billing, billingDetails);
    }

    /**
     * Allows the ability to resubscribe to a plan.
     * @param {any} billingDetails
     * @returns {Observable<any>}
     */
    updateSubscription(billingDetails) {
        return this.put(this.urls.billing, billingDetails);
    }

    cancelSubscription(planId: SubscriptionPlan) {
        return this.delete(this.urls.billingPlan.replace(':planId', planId));
    }

    fetchBillingReport(startMonth: string, endMonth: string) {
        const requestUrl: string = this.urls.billingReport
            .replace(':startMonth', startMonth)
            .replace(':endMonth', endMonth);
        return this.get(requestUrl);
    }

    exportBillingStatement(invoiceId: string) {
        const requestUrl = this.urls.billingStatement
            .replace(':invoiceId', invoiceId);

        const options = {
            headers: new HttpHeaders().set('Accept', 'application/csv'),
            responseType: 'arraybuffer',
        };

        return this.get(requestUrl, options);
    }
}